import { useEffect, useMemo, useRef } from 'react';
import {
  keepPreviousData,
  useQueries,
  useQuery,
  useQueryClient,
  type Query,
  type QueryClient,
} from '@tanstack/react-query';
import {
  getApiV3Cookbooks,
  getApiV3CustomerPairings,
  getApiV3Pairings,
  getApiV3Products,
  StatusCode,
  type CookbookV3,
  type PairingListResultV3,
  type Product,
} from '@hungryroot/rest';
import { CUSTOMER_HOME_FEED_CACHE_LIFETIME_MS } from '@hungryroot/rest-hooks';
import { collectFirstCookbookIds } from '../homeFeedCookbookFields';
import { type HomeFeedCrmItemRef } from '../homeFeedCrmEntry';
import {
  getHomeFeedHydrationChunks,
  type HomeFeedHydrationChunk,
} from '../homeFeedHydrationChunks';
import {
  FEED_PAIRING_HYDRATION_EXPANDS,
  FEED_PRODUCT_HYDRATION_EXPANDS,
} from '../homeFeedHydrationExpands';
import {
  getHomeFeedHydrationRetryDelayMs,
  HOME_FEED_HYDRATION_MAX_RETRY_ATTEMPTS,
} from '../homeFeedHydrationRetry';
import {
  collectIngredientProductIds,
  dedupeIds,
} from '../homeFeedHydrationUtils';
import { buildPairingOutOfStockById } from '../pairingMerchandising';
import { HOME_FEED_ITEM_TYPES } from '../types';
import { usePairingMerchandisingInventoryParams } from './usePairingMerchandisingInventoryParams';

// Freshness only. The cards are kept as long as the feed they belong to — see
// `CUSTOMER_HOME_FEED_CACHE_LIFETIME_MS` — or a return to Home would find the
// list cached but its first cards missing, and show the skeleton anyway. Past
// this they revalidate in the background, behind the cards already on screen.
const TIME_LIMIT_15_MINUTES = 15 * 60 * 1000;
const HYDRATION_QUERY_KEYS = [
  'MobileHomeFeedChunkPairings',
  'MobileHomeFeedChunkProducts',
  'MobileHomeFeedChunkPairingMerchandising',
  'MobileHomeFeedCookbooks',
] as const;
const CRM_PINNED_CHUNK_KEY_PREFIX = 'crm-pinned-' as const;
const [
  MOBILE_HOME_FEED_CHUNK_PAIRINGS_QUERY_KEY,
  MOBILE_HOME_FEED_CHUNK_PRODUCTS_QUERY_KEY,
  MOBILE_HOME_FEED_CHUNK_PAIRING_MERCH_QUERY_KEY,
  MOBILE_HOME_FEED_COOKBOOKS_QUERY_KEY,
] = HYDRATION_QUERY_KEYS;
// Each of these is keyed `[name, chunkKey, ...]`, so one chunk's set can be
// matched by its first two elements.
const PER_CHUNK_QUERY_KEYS = [
  MOBILE_HOME_FEED_CHUNK_PAIRINGS_QUERY_KEY,
  MOBILE_HOME_FEED_CHUNK_PRODUCTS_QUERY_KEY,
  MOBILE_HOME_FEED_CHUNK_PAIRING_MERCH_QUERY_KEY,
] as const;

interface UseMobileHomeFeedChunkHydrationOptions {
  chunkSize: number;
  enabled: boolean;
  pinnedItemRef?: HomeFeedCrmItemRef | null;
  pages:
    | ReadonlyArray<{
        results: readonly { item_id: number; item_type: string }[];
      }>
    | undefined;
}

export function useMobileHomeFeedChunkHydration({
  chunkSize,
  enabled,
  pinnedItemRef = null,
  pages,
}: UseMobileHomeFeedChunkHydrationOptions) {
  const chunks = useMemo(() => {
    const feedChunks = getHomeFeedHydrationChunks({
      chunkSize,
      pages,
    });

    if (pinnedItemRef === null) return feedChunks;

    return [buildPinnedCrmHydrationChunk({ pinnedItemRef }), ...feedChunks];
  }, [chunkSize, pages, pinnedItemRef]);

  const pairingsQueries = useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: [
        MOBILE_HOME_FEED_CHUNK_PAIRINGS_QUERY_KEY,
        chunk.chunkKey,
        chunk.pairingIds,
      ] as const,
      queryFn: async () => {
        const res = await getApiV3Pairings({
          ids: chunk.pairingIds,
          expand: [...FEED_PAIRING_HYDRATION_EXPANDS],
          limit: chunk.pairingIds.length,
        });

        if (res.status !== StatusCode.SuccessOK) {
          throw new Error(JSON.stringify(res.data, null, 2));
        }

        return res.data;
      },
      enabled: enabled && chunk.pairingIds.length > 0,
      placeholderData: keepPreviousData,
      staleTime: TIME_LIMIT_15_MINUTES,
      gcTime: CUSTOMER_HOME_FEED_CACHE_LIFETIME_MS,
      // Paced instead — see `revalidateStaleHydrationOneChunkAtATime`.
      refetchOnMount: false,
      retry: 0,
    })),
  });

  const productsQueries = useQueries({
    queries: chunks.map((chunk, index) => {
      const pairingsQuery = pairingsQueries[index];
      const productIds = getChunkProductIds({
        chunk,
        pairings: pairingsQuery?.data?.results,
      });
      const arePairingsReady =
        chunk.pairingIds.length === 0 || pairingsQuery?.isSuccess === true;

      return {
        queryKey: [
          MOBILE_HOME_FEED_CHUNK_PRODUCTS_QUERY_KEY,
          chunk.chunkKey,
          productIds,
        ] as const,
        queryFn: async () => {
          const res = await getApiV3Products({
            ids: productIds,
            expand: [...FEED_PRODUCT_HYDRATION_EXPANDS],
            limit: productIds.length,
          });

          if (res.status !== StatusCode.SuccessOK) {
            throw new Error(JSON.stringify(res.data, null, 2));
          }

          return res.data;
        },
        enabled: enabled && productIds.length > 0 && arePairingsReady,
        placeholderData: keepPreviousData,
        staleTime: TIME_LIMIT_15_MINUTES,
        gcTime: CUSTOMER_HOME_FEED_CACHE_LIFETIME_MS,
        // Paced instead — see `revalidateStaleHydrationOneChunkAtATime`.
        refetchOnMount: false,
        retry: 0,
      };
    }),
  });

  // Out-of-stock state for recipes, which the display reads cannot answer:
  // `/api/v3/pairings/` carries no `is_merchandized`, and the ingredient products
  // above are fetched without inventory params, so their flag reports *global*
  // merchandising rather than availability for this customer's next order. Only
  // `/api/v3/customers/self/pairings/` answers the actual question, which is why
  // the cookbook runs the same second query alongside its pairing details.
  //
  // Deliberately independent of `pairingsQueries`: the ids come from the feed
  // page, not from the pairing details, so this runs concurrently rather than
  // adding a stage to the hydration waterfall. Its only dependency is the order.
  const inventoryParams = usePairingMerchandisingInventoryParams();
  const pairingMerchQueries = useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: [
        MOBILE_HOME_FEED_CHUNK_PAIRING_MERCH_QUERY_KEY,
        chunk.chunkKey,
        chunk.pairingIds,
        inventoryParams,
      ] as const,
      queryFn: async () => {
        if (inventoryParams === null) {
          throw new Error('Missing inventory params for pairing merchandising');
        }

        const res = await getApiV3CustomerPairings({
          ids: chunk.pairingIds,
          // Load-bearing, despite reading as a sort: the endpoint only computes
          // `is_merchandized` when it is asked to order by it. Without this
          // every result comes back `is_merchandized: null`, which resolves to
          // "in stock" and silently reports `out_of_stock: false` for every
          // recipe. Verified against pairing 500200 (Diner-Style Salisbury
          // Steak), which is out of stock via an ingredient: `null` without this
          // param, `false` with it. The cookbook passes it for the same reason.
          ordering: '-is_merchandized',
          orderDate: inventoryParams.orderDate,
          origin: inventoryParams.origin,
          // Every requested id in one page. Without this the endpoint's default
          // page size would make an id "absent" merely for having paged out,
          // which `resolvePairingOutOfStock` would then report as unknown.
          limit: chunk.pairingIds.length,
        });

        if (res.status !== StatusCode.SuccessOK) {
          throw new Error(JSON.stringify(res.data, null, 2));
        }

        return res.data;
      },
      enabled:
        enabled && chunk.pairingIds.length > 0 && inventoryParams !== null,
      placeholderData: keepPreviousData,
      staleTime: TIME_LIMIT_15_MINUTES,
      gcTime: CUSTOMER_HOME_FEED_CACHE_LIFETIME_MS,
      // Paced instead — see `revalidateStaleHydrationOneChunkAtATime`.
      refetchOnMount: false,
      retry: 0,
    })),
  });

  useRetryFailedChunkHydrationQueries({
    queries: [
      ...pairingsQueries.map((query, index) => ({
        id: `pairings:${chunks[index]?.chunkKey ?? index}`,
        hasIds: (chunks[index]?.pairingIds.length ?? 0) > 0,
        isError: query.isError,
        isFetching: query.isFetching,
        isSuccess: query.isSuccess,
        refetch: query.refetch,
      })),
      ...productsQueries.map((query, index) => ({
        id: `products:${chunks[index]?.chunkKey ?? index}`,
        hasIds:
          getChunkProductIds({
            chunk: chunks[index] ?? emptyChunk(),
            pairings: pairingsQueries[index]?.data?.results,
          }).length > 0,
        isError: query.isError,
        isFetching: query.isFetching,
        isSuccess: query.isSuccess,
        refetch: query.refetch,
      })),
      // Deliberately NOT the merch queries. That retry loop exists to get cards
      // on screen, and it refetches hard enough to re-render the whole feed;
      // merch is analytics-only, so a failure there degrades to
      // `out_of_stock: null` rather than earning three immediate retries and the
      // render churn that comes with them.
    ],
  });

  const pairingsById = mergePairingsById({
    pairingsQueryData: pairingsQueries.map((query) => query.data),
  });
  const productsById = mergeProductsById({
    productsQueryData: productsQueries.map((query) => query.data),
  });
  // Rebuilt each render, like `pairingsById`/`productsById` above — the chunk
  // count grows as the feed pages in, so anything keyed on a per-chunk dependency
  // array would change size between renders. `stabilizeDisplayItems` downstream
  // is what keeps card identities stable across these recomputes.
  const pairingOutOfStockById = buildPairingOutOfStockById(
    pairingMerchQueries.map((query) => query.data?.results)
  );
  const cookbookIds = useMemo(
    () =>
      collectFirstCookbookIds({
        pairings: [...pairingsById.values()],
        products: [...productsById.values()],
      }),
    [pairingsById, productsById]
  );
  const arePairingsAndProductsReady =
    pairingsQueries.every(
      (query, index) =>
        (chunks[index]?.pairingIds.length ?? 0) === 0 || query.isSuccess
    ) &&
    productsQueries.every((query, index) => {
      const productIds = getChunkProductIds({
        chunk: chunks[index] ?? emptyChunk(),
        pairings: pairingsQueries[index]?.data?.results,
      });

      return productIds.length === 0 || query.isSuccess;
    });
  const cookbooksQuery = useQuery({
    queryKey: [MOBILE_HOME_FEED_COOKBOOKS_QUERY_KEY, cookbookIds] as const,
    queryFn: async () => {
      const res = await getApiV3Cookbooks({
        ids: cookbookIds,
        limit: cookbookIds.length,
      });

      if (res.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(res.data, null, 2));
      }

      return res.data;
    },
    enabled: enabled && arePairingsAndProductsReady && cookbookIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: TIME_LIMIT_15_MINUTES,
    gcTime: CUSTOMER_HOME_FEED_CACHE_LIFETIME_MS,
    // Paced instead — see `revalidateStaleHydrationOneChunkAtATime`.
    refetchOnMount: false,
    retry: 0,
  });
  const cookbooksById = useMemo(
    () => mergeCookbooksById({ cookbooks: cookbooksQuery.data?.results }),
    [cookbooksQuery.data?.results]
  );

  // With no expiry, cards for a feed that has been replaced would otherwise
  // stay in memory for the life of the page. Every chunk of the feed on screen
  // is observed above, so anything inactive belongs to an old one. Runs after
  // the queries above have switched keys.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;

    for (const queryKey of HYDRATION_QUERY_KEYS) {
      queryClient.removeQueries({ queryKey: [queryKey], type: 'inactive' });
    }
  }, [chunks, cookbookIds, enabled, queryClient]);

  // Rechecks stale cards when Home mounts, one chunk at a time from the top.
  // Left to react-query, a return to Home after they went stale refetched every
  // loaded chunk at once — up to three requests per 30 cards, dozens for a
  // customer who had scrolled far. The cards stay on screen while they are
  // checked, so there is no reason to fire them together. Mount-only: chunks
  // loaded later arrive fresh.
  const chunksAtMountRef = useRef(chunks);
  useEffect(() => {
    if (!enabled) return;

    let isUnmounted = false;
    void revalidateStaleHydrationOneChunkAtATime({
      queryClient,
      chunks: chunksAtMountRef.current,
      isCancelled: () => isUnmounted,
    });

    return () => {
      isUnmounted = true;
    };
  }, [enabled, queryClient]);
  const firstFeedChunkIndex = chunks.findIndex(
    (chunk) => !chunk.chunkKey.startsWith(CRM_PINNED_CHUNK_KEY_PREFIX)
  );
  const firstFeedChunk = chunks[firstFeedChunkIndex] ?? emptyChunk();
  const isInitialHydrationLoading =
    firstFeedChunkIndex >= 0 &&
    !isChunkHydrated({
      chunk: firstFeedChunk,
      pairingsQuery: {
        data: pairingsQueries[firstFeedChunkIndex]?.data,
        isError: pairingsQueries[firstFeedChunkIndex]?.isError ?? false,
      },
      productsQuery: {
        data: productsQueries[firstFeedChunkIndex]?.data,
        isError: productsQueries[firstFeedChunkIndex]?.isError ?? false,
      },
    });
  const isHydrationError =
    pairingsQueries.some(
      (query) => query.isError && query.data === undefined
    ) ||
    productsQueries.some((query) => query.isError && query.data === undefined);

  return {
    chunks,
    pairingsById,
    productsById,
    // Intentionally absent from `isInitialHydrationLoading` and
    // `isHydrationError`: stock state is analytics-only today, so waiting on it
    // would delay first paint and a failed merch read would blank the feed. An
    // unresolved id reports `out_of_stock: null` instead.
    pairingOutOfStockById,
    cookbooksById,
    isInitialHydrationLoading,
    isHydrationError,
  };
}

/** Cached, stale and not already being fetched. */
function isRevalidatable(query: Query): boolean {
  return (
    query.state.data !== undefined &&
    query.state.fetchStatus === 'idle' &&
    query.isStale()
  );
}

async function revalidateStaleHydrationOneChunkAtATime({
  queryClient,
  chunks,
  isCancelled,
}: {
  queryClient: QueryClient;
  chunks: readonly HomeFeedHydrationChunk[];
  isCancelled: () => boolean;
}): Promise<void> {
  // `cancelRefetch: false` leaves a fetch already in flight alone rather than
  // restarting it. A failure keeps the cached cards: refetchQueries never throws.
  const refetchOptions = { cancelRefetch: false };

  for (const chunk of chunks) {
    if (isCancelled()) return;

    await Promise.all(
      PER_CHUNK_QUERY_KEYS.map((name) =>
        queryClient.refetchQueries(
          {
            queryKey: [name, chunk.chunkKey],
            type: 'active',
            predicate: isRevalidatable,
          },
          refetchOptions
        )
      )
    );
  }

  if (isCancelled()) return;
  await queryClient.refetchQueries(
    {
      queryKey: [MOBILE_HOME_FEED_COOKBOOKS_QUERY_KEY],
      type: 'active',
      predicate: isRevalidatable,
    },
    refetchOptions
  );
}

function getChunkProductIds({
  chunk,
  pairings,
}: {
  chunk: HomeFeedHydrationChunk;
  pairings: readonly PairingListResultV3[] | undefined;
}): number[] {
  return dedupeIds([
    ...chunk.productIds,
    ...collectIngredientProductIds(pairings),
  ]);
}

interface PairingsChunkQueryResult {
  data?: {
    results?: readonly PairingListResultV3[];
  };
  isError: boolean;
}

interface ProductsChunkQueryResult {
  data?: {
    results?: readonly Product[];
  };
  isError: boolean;
}

function isChunkHydrated({
  chunk,
  pairingsQuery,
  productsQuery,
}: {
  chunk: HomeFeedHydrationChunk;
  pairingsQuery: PairingsChunkQueryResult | undefined;
  productsQuery: ProductsChunkQueryResult | undefined;
}): boolean {
  if (
    chunk.pairingIds.length > 0 &&
    pairingsQuery?.data === undefined &&
    !pairingsQuery?.isError
  ) {
    return false;
  }

  const productIds = getChunkProductIds({
    chunk,
    pairings: pairingsQuery?.data?.results,
  });

  if (
    productIds.length > 0 &&
    productsQuery?.data === undefined &&
    !productsQuery?.isError
  ) {
    return false;
  }

  return true;
}

function mergePairingsById({
  pairingsQueryData,
}: {
  pairingsQueryData: ReadonlyArray<
    | {
        results?: readonly PairingListResultV3[];
      }
    | undefined
  >;
}): Map<number, PairingListResultV3> {
  const pairingsById = new Map<number, PairingListResultV3>();

  for (const data of pairingsQueryData) {
    for (const pairing of data?.results ?? []) {
      pairingsById.set(pairing.id, pairing);
    }
  }

  return pairingsById;
}

function mergeProductsById({
  productsQueryData,
}: {
  productsQueryData: ReadonlyArray<
    { results?: readonly Product[] } | undefined
  >;
}): Map<number, Product> {
  const productsById = new Map<number, Product>();

  for (const data of productsQueryData) {
    for (const product of data?.results ?? []) {
      productsById.set(product.id, product);
    }
  }

  return productsById;
}

function mergeCookbooksById({
  cookbooks,
}: {
  cookbooks: readonly CookbookV3[] | undefined;
}): Map<number, CookbookV3> {
  return new Map((cookbooks ?? []).map((cookbook) => [cookbook.id, cookbook]));
}

function emptyChunk(): HomeFeedHydrationChunk {
  return {
    chunkKey: 'empty',
    feedItemsWithIndices: [],
    pairingIds: [],
    productIds: [],
  };
}

function buildPinnedCrmHydrationChunk({
  pinnedItemRef,
}: {
  pinnedItemRef: HomeFeedCrmItemRef;
}): HomeFeedHydrationChunk {
  return {
    chunkKey: `${CRM_PINNED_CHUNK_KEY_PREFIX}${pinnedItemRef.itemType}-${pinnedItemRef.itemId}`,
    feedItemsWithIndices: [],
    pairingIds:
      pinnedItemRef.itemType === HOME_FEED_ITEM_TYPES.pairing
        ? [pinnedItemRef.itemId]
        : [],
    productIds:
      pinnedItemRef.itemType === HOME_FEED_ITEM_TYPES.product
        ? [pinnedItemRef.itemId]
        : [],
  };
}

interface RetryableChunkQuery {
  id: string;
  hasIds: boolean;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  refetch: () => Promise<unknown>;
}

function useRetryFailedChunkHydrationQueries({
  queries,
}: {
  queries: RetryableChunkQuery[];
}): void {
  const retryAttemptsRef = useRef(new Map<string, number>());
  const refetchByIdRef = useRef(new Map<string, () => Promise<unknown>>());
  const queriesRef = useRef(queries);
  queriesRef.current = queries;

  for (const query of queries) {
    refetchByIdRef.current.set(query.id, query.refetch);
    if (query.isSuccess) {
      retryAttemptsRef.current.delete(query.id);
    }
  }

  const retryStateKey = queries
    .map(
      (query) =>
        `${query.id}:${query.hasIds}:${query.isError}:${query.isFetching}:${query.isSuccess}`
    )
    .join('|');

  useEffect(() => {
    const timeoutIds: number[] = [];

    for (const query of queriesRef.current) {
      if (!query.hasIds || !query.isError || query.isFetching) continue;

      const attempt = retryAttemptsRef.current.get(query.id) ?? 0;
      if (attempt >= HOME_FEED_HYDRATION_MAX_RETRY_ATTEMPTS) continue;

      const timeoutId = window.setTimeout(() => {
        retryAttemptsRef.current.set(query.id, attempt + 1);
        void refetchByIdRef.current.get(query.id)?.();
      }, getHomeFeedHydrationRetryDelayMs(attempt));
      timeoutIds.push(timeoutId);
    }

    return () => {
      for (const timeoutId of timeoutIds) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [retryStateKey]);
}
